import '@hotwired/turbo-rails'
import * as Turbo from '@hotwired/turbo'

import TurboPower from 'turbo_power'
import './frame_spinner'
import { keepTrayAcrossMorph, showToast, toast } from './flash_toast'
import { applyConfirmPayload, parseConfirmPayload } from '../utils/turbo_confirm'
import { holdDialogStack } from '../utils/dialog_stack_morph'
import { staleStreamAction } from './stale_stream_action'
import { visitStreamAction } from './visit_stream_action'
import { focusElementStreamAction } from './focus_element_stream_action'
TurboPower.initialize(Turbo.StreamActions)

Turbo.setProgressBarDelay(500)

const supportsViewTransitions = () =>
  typeof document.startViewTransition === 'function' &&
  !window.matchMedia('(prefers-reduced-motion: reduce)').matches

const VIEW_TRANSITION_TARGET_SELECTOR = '[data-view-transition="true"]'

const hasViewTransitionTarget = (element) =>
  Boolean(
    element &&
    (element.matches?.(VIEW_TRANSITION_TARGET_SELECTOR) ||
      element.querySelector?.(VIEW_TRANSITION_TARGET_SELECTOR))
  )

document.addEventListener('turbo:before-render', event => {
  if (!supportsViewTransitions()) return
  if (!hasViewTransitionTarget(document.body) && !hasViewTransitionTarget(event.detail.newBody)) return

  event.preventDefault()
  document.startViewTransition(() => event.detail.resume())
})

document.addEventListener('turbo:before-frame-render', event => {
  if (!supportsViewTransitions()) return

  const defaultRender = event.detail.render
  event.detail.render = (currentElement, newElement) => {
    if (!hasViewTransitionTarget(currentElement) && !hasViewTransitionTarget(newElement)) {
      return defaultRender(currentElement, newElement)
    }

    document.startViewTransition(() => defaultRender(currentElement, newElement))
  }
})

const SEARCH_INPUT_SELECTOR = '[data-search-target="input"]'

let composingIn = null

document.addEventListener('compositionstart', event => { composingIn = event.target }, true)
document.addEventListener('compositionend', () => { composingIn = null }, true)

const restoreCaretAfterStreamRender = ({ node, id, start, end }) => {
  const input = document.getElementById(id)
  if (!input || input === node) return

  const active = document.activeElement
  if (active !== document.body && active !== input) return

  input.focus({ preventScroll: true })
  input.setSelectionRange(start, end)
}

// Bardo swaps the live `data-turbo-permanent` node for a clone on a microtask
// after the stream renders, so this must stay the last thing touching focus.
document.addEventListener('turbo:before-stream-render', event => {
  const input = document.activeElement
  if (!input?.id || input === composingIn || !input.matches(SEARCH_INPUT_SELECTOR)) return

  const typing = { node: input, id: input.id, start: input.selectionStart, end: input.selectionEnd }
  const render = event.detail.render

  event.detail.render = async (streamElement) => {
    await render(streamElement)
    restoreCaretAfterStreamRender(typing)
  }
})

// A frame refuses to navigate to the URL it already holds, so leaving `src` on
// the emptied `#dialog` frame would make reopening the same dialog a silent
// no-op. Clearing it here rather than from the dialog's own `connect` keeps it
// out of the frame's in-flight render.
document.addEventListener('turbo:frame-load', event => {
  if (event.target.id === 'dialog') event.target.removeAttribute('src')
})

// A response without the frame leaves Turbo's "Content missing" in the layout
// and throws past it, with nothing to say the dialog failed. A production 5xx
// arrives this way too — only the dev rescue page opts out, through its own
// `turbo-visit-control`. `src` goes as well, or the retry is a no-op.
const reportDialogFailure = (frame) => {
  frame.removeAttribute('src')

  const template = document.getElementById('dialog_error_toast')
  if (template) showToast(template.content.cloneNode(true))
}

// If a frame is missing, it's likely because the server redirected to a new location
document.addEventListener('turbo:frame-missing', event => {
  if (event.detail.response.redirected) {
    event.preventDefault()
    event.detail.visit(event.detail.response)
    return
  }

  if (event.target.id !== 'dialog') return

  event.preventDefault()
  reportDialogFailure(event.target)
})

document.addEventListener('turbo:fetch-request-error', event => {
  if (event.target?.id === 'dialog') reportDialogFailure(event.target)
})

Turbo.setConfirmMethod((message) => {
  const dialog = document.getElementById('turbo-confirm')

  applyConfirmPayload(dialog, parseConfirmPayload(message))

  // Closing without a result leaves `returnValue` alone, and Escape does
  // exactly that — so a stale 'confirm' from an earlier prompt would answer
  // this one.
  dialog.returnValue = ''
  dialog.showModal()

  return new Promise((resolve) => {
    const listeners = new AbortController()

    dialog.addEventListener('close', () => {
      listeners.abort()
      resolve(dialog.returnValue === 'confirm')
    }, { signal: listeners.signal })

    dialog.addEventListener('click', (event) => {
      if (event.target.nodeName === 'DIALOG') dialog.close('cancel')
    }, { signal: listeners.signal })
  })
})

window.addEventListener('turbo:before-morph-element', (event) => {
  if (event.target.id === 'modal') {
    event.target.setAttribute('src', '')
  }

  if (holdDialogStack(event)) return

  keepTrayAcrossMorph(event)
})

Turbo.StreamActions.toast = toast

Turbo.StreamActions.insert_in_container = function () {
  const containerId = this.getAttribute('container')
  const position = parseInt(this.getAttribute('position'))

  const container = document.getElementById(containerId)
  if (!container) return

  const children = Array.from(container.children)
  const template = this.templateContent

  if (position >= children.length) {
    container.appendChild(template)
  } else {
    container.insertBefore(template, children[position])
  }
}

Turbo.StreamActions.render_at_position = function () {
  const containerId = this.getAttribute('container')
  const position = parseInt(this.getAttribute('position'))
  const template = this.templateContent

  const container = document.getElementById(containerId)
  if (!container) return

  // Get the first element from template to check its ID
  const newElement = template.firstElementChild
  if (!newElement || !newElement.id) return

  // Check if element with this ID already exists
  const existingElement = document.getElementById(newElement.id)

  // If element exists, remove it first
  if (existingElement) {
    existingElement.remove()
  }

  // Insert at the specified position
  const children = Array.from(container.children)

  if (position >= children.length) {
    container.appendChild(template)
  } else {
    container.insertBefore(template, children[position])
  }
}

Turbo.StreamActions.stale = staleStreamAction

Turbo.StreamActions.visit = visitStreamAction

Turbo.StreamActions.focus_element = focusElementStreamAction
